import { useEffect } from 'react';

/**
 * Предупреждение при уходе с несохранёнными изменениями (0072, шаг 2).
 *
 * Два пути ухода — два перехвата:
 *
 * * закрытие вкладки, перезагрузка, внешний адрес — `beforeunload`;
 * * внутренняя ссылка приложения (меню раздела, шапка) — клик по `<a>` в фазе
 *   ПЕРЕХВАТА на `document`.
 *
 * 🔴 Почему не `useBlocker`: LMS стоит на `BrowserRouter` react-router 6.2, где
 * блокировки навигации нет вовсе (она появилась только в data-router 6.7+).
 * Слушатель на `document` в фазе перехвата срабатывает РАНЬШЕ обработчика
 * `<Link>` (React 17 вешает события на корень приложения), а `Link` не
 * переходит, если `defaultPrevented`.
 *
 * Ссылки в новую вкладку, с модификаторами и якоря на этой же странице не
 * перехватываются: со страницы они не уводят.
 */
export const useUnsavedChangesGuard = (dirty: boolean, message: string) => {
  useEffect(() => {
    if (!dirty) return undefined;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = message;
      return message;
    };

    const onClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = (event.target as HTMLElement | null)?.closest?.('a[href]');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.target && anchor.target !== '_self') return;
      const url = new URL(anchor.href, window.location.href);
      const samePage =
        url.origin === window.location.origin &&
        url.pathname === window.location.pathname &&
        url.search === window.location.search;
      if (samePage) return;
      // eslint-disable-next-line no-alert
      if (!window.confirm(message)) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClick, true);
    };
  }, [dirty, message]);
};
